import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { ButtonColor } from "@/components/Button/types";

import { RandomValueModal } from "../../../List/components/CustomColumns/Modals/RandomValueModal";
import { ColumnConfig } from "../../../List/schema/columnConfig";

import { useEditorCursorInsertion } from "../AiSnippets/hooks/useEditorCursorInsertion";
import {
	rewriteAllTinyMceEditors,
	updateColumnPlaceholdersInTinyMce,
} from "../AiSnippets/utils/rewriteEditorContent";
import {
	buildColumnTokenRewriter,
	buildSnippetToken,
	extractInsertedColumnIds,
} from "../AiSnippets/utils/snippetTokens";
import { collectNodeMessageContents } from "../Variables/utils/variableRows";

import { SpintaxSectionCard } from "./components/SpintaxSectionCard";
import { buildSpintaxRows, SpintaxRow } from "./utils/spintaxRows";

import type { List } from "../../../List/schema/list";

import type { MyNode } from "../../pages/CampaignMessagingOverview/CampaignMessagingOverview";
import type {
	EmailNodeConfig,
	LinkedinInviteNodeConfig,
	LinkedinNodeConfig,
	LinkedinInmailNodeConfig,
} from "../../../List/components/CampaignOverview/components/nodes";
import type { LinkedinVisitNodeConfig } from "../../../List/components/CampaignOverview/components/nodes/LinkedinVisitNode";

export interface SpintaxHandle {
	openCreateModal: () => void;
}

interface SpintaxProps {
	listData?: List;
	selectedNode?:
		| MyNode
		| EmailNodeConfig
		| LinkedinInviteNodeConfig
		| LinkedinNodeConfig
		| LinkedinInmailNodeConfig
		| LinkedinVisitNodeConfig;
	withPersistance?: (result: string[]) => void;
}

/**
 * "Spintax" panel of the campaign node editor: lists the RANDOM_VALUE columns
 * of the list and lets the user insert their placeholder at the editor cursor
 * (click or drag), create new ones and edit existing ones via the same
 * RandomValueModal used by the custom column flow.
 */
export const Spintax = forwardRef<SpintaxHandle, SpintaxProps>(function Spintax(
	{ listData, selectedNode, withPersistance },
	ref
) {
	const { t } = useTranslation();
	const { insertAtCursor } = useEditorCursorInsertion();

	const [showModal, setShowModal] = useState<boolean>(false);
	const [showConfirm, setShowConfirm] = useState<boolean>(false);
	const [editingColumn, setEditingColumn] = useState<ColumnConfig | null>(null);

	const message = useMemo(() => selectedNode?.data?.message ?? "", [selectedNode?.data?.message]);

	const premiumMessage = useMemo(
		() => selectedNode?.data?.premiumMessage ?? "",
		[selectedNode?.data?.premiumMessage]
	);

	const insertedColumnIds = useMemo(
		() => extractInsertedColumnIds(collectNodeMessageContents(selectedNode?.data)),
		[selectedNode?.data]
	);

	const rows = useMemo(
		() => buildSpintaxRows({ listColumns: listData?.columns, insertedColumnIds }),
		[listData?.columns, insertedColumnIds]
	);

	const inUseCount = useMemo(() => rows.filter((row) => row.isInserted).length, [rows]);

	const handleRowInsert = useCallback(
		(row: SpintaxRow) => {
			const token = buildSnippetToken(row.columnId, row.name);

			if (insertAtCursor(token) || !withPersistance) {
				return;
			}

			// Fallback: append to the message when no editor was focused yet.
			const next = message ? `${message} ${token}` : token;

			if (selectedNode?.type === "linkedinInviteNode") {
				// The pages map results[0] to premiumMessage for invite nodes.
				withPersistance([premiumMessage, next]);
			} else {
				withPersistance([next]);
			}
		},
		[insertAtCursor, withPersistance, message, premiumMessage, selectedNode?.type]
	);

	const handleCreateNew = useCallback(() => {
		setEditingColumn(null);
		setShowModal(true);
	}, []);

	useImperativeHandle(ref, () => ({ openCreateModal: handleCreateNew }), [handleCreateNew]);

	const handleRowConfigure = useCallback((row: SpintaxRow) => {
		setEditingColumn(row.column);
		setShowModal(true);
	}, []);

	const closeModal = useCallback(() => {
		setShowModal(false);
		setShowConfirm(false);
		setEditingColumn(null);
	}, []);

	const handleColumnRenamed = useCallback(
		(columnId: number, newName: string) => {
			const rewrite = buildColumnTokenRewriter(columnId, newName);

			// Flip the visible chips in every TinyMCE editor immediately, then run
			// a full setContent round-trip so the serialized content matches the
			// new tokens (same approach as AiSnippets.handleColumnRenamed).
			updateColumnPlaceholdersInTinyMce(columnId, newName);
			const edits = rewriteAllTinyMceEditors(rewrite);

			if (!withPersistance) {
				return;
			}

			// Fall back to rewriting the closure values when no editor matched —
			// covers tests, collapsed editors, etc.
			let resolvedMessage = rewrite(message);
			let resolvedPremium = rewrite(premiumMessage);

			for (const edit of edits) {
				if (premiumMessage && edit.previous === premiumMessage) {
					resolvedPremium = edit.next;
				} else if (message && edit.previous === message) {
					resolvedMessage = edit.next;
				}
			}

			if (resolvedMessage === message && resolvedPremium === premiumMessage) {
				return;
			}

			// Mirror the parent's withPersistance ordering: with a premium message
			// present, results[0] is the premium and results[1] the main message.
			if (premiumMessage) {
				withPersistance([resolvedPremium, resolvedMessage]);
			} else {
				withPersistance([resolvedMessage]);
			}
		},
		[withPersistance, message, premiumMessage]
	);

	return (
		<div className="flex flex-col w-full mt-4">
			<SpintaxSectionCard
				inUseCount={inUseCount}
				rows={rows}
				onCreateNew={handleCreateNew}
				onRowConfigure={handleRowConfigure}
				onRowInsert={handleRowInsert}
			/>

			{showModal && listData?.id && (
				<RandomValueModal
					key={editingColumn?.id ?? "new"}
					column={editingColumn ?? undefined}
					columnId={editingColumn?.id}
					listId={listData.id}
					open={showModal}
					setOpen={(value) => {
						setShowModal(value);

						if (!value) {
							setEditingColumn(null);
						}
					}}
					setShowConfirm={setShowConfirm}
					onColumnRenamed={handleColumnRenamed}
				/>
			)}

			{showConfirm && (
				<Modal
					handleClose={() => setShowConfirm(false)}
					handleSave={async () => closeModal()}
					isOpened={showConfirm}
					size="xs"
					submitButtonColor={ButtonColor.ACTION}
					submitButtonText={t("basics.confirm")}
					title={t("basics.confirm")}
				>
					<>{t("basics.confirmationClose")} </>
				</Modal>
			)}
		</div>
	);
});
